import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import {
  CellClassParams,
  ColDef,
  GridApi,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import {
  ButtonPrimary,
  ButtonPrimary1,
  CellRenderer,
  HStack,
  ValidityCellRenderer,
  VStack,
} from "../../components/utils";
import AlertDialog from "../../components/shared/AlertDialog";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import {
  downloadBlobObject,
  convertBase64ToBlob,
} from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";
import { getBulkResignationTemplate } from "../../api/Esop";
import { useUploadBulkResignationImport } from "../../queries/employees";
import { useCompanyStore } from "../../store";
import { ValidationObj } from "../../types/Grant";
import SeparationReasons from "../../constants/SeparationReasons";
import {
  BulkResignationImportResponse,
  ResignationRowReversionWarning,
} from "../../types/Employee";

type ImportResult = BulkResignationImportResponse;

function ReversionWarningPanel({
  warnings,
}: {
  warnings: ResignationRowReversionWarning[];
}) {
  const [expanded, setExpanded] = React.useState(false);
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md mx-3 mb-4 mt-4">
      <button
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        onClick={() => setExpanded((p) => !p)}
      >
        <Icon
          icon="mdi:alert-outline"
          className="text-amber-500 shrink-0"
          height={18}
        />
        <span className="flex-1 text-sm font-medium text-amber-800">
          {warnings.length} employee{warnings.length > 1 ? "s" : ""} will have
          vested options reverted due to pause-vesting date — review before
          confirming
        </span>
        <Icon
          icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="text-amber-600 shrink-0"
          height={18}
        />
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-amber-700 border-b border-amber-200">
                <th className="py-1 pr-4 font-medium">Employee HR ID</th>
                <th className="py-1 pr-4 font-medium">Grant</th>
                <th className="py-1 pr-4 font-medium">Grant Date</th>
                <th className="py-1 font-medium">Options to Revert</th>
              </tr>
            </thead>
            <tbody>
              {warnings.flatMap((w) =>
                w.affectedGrants.map((g, gi) => (
                  <tr
                    key={`${w.rowIndex}-${gi}`}
                    className="border-b border-amber-100 last:border-0"
                  >
                    <td className="py-1 pr-4 text-gray-700">
                      {gi === 0 ? w.hrId : ""}
                    </td>
                    <td className="py-1 pr-4 text-gray-700">
                      {g.grantIdentifier}
                    </td>
                    <td className="py-1 pr-4 text-gray-700">
                      {new Date(g.dateOfGrant).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="py-1 text-gray-700">{g.optionsToRevert}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function getEffectiveDisplayValue(value: any): string {
  if (value && typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.substring(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  return value != null ? String(value) : "";
}

// Helper function to map reason values to their display labels
function getReasonDisplayLabel(value: string): string {
  const reason = SeparationReasons.find((r) => r.value === value);
  return reason ? reason.label : value;
}

function isDateColumnName(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes("date") || n.includes("day");
}

function isReasonColumnName(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes("resignation reason") || n === "separation type";
}

function isRowValid(row: ValidationObj[]) {
  return !row?.filter((d) => d.name !== "validity")?.some((d) => !d.valid);
}

function buildColumns(
  data: ValidationObj[][],
  gridApi: React.MutableRefObject<GridApi | any>,
  onErrorsChange: (hasErrors: boolean) => void
): ColDef[] {
  if (!data || data.length === 0) return [];

  const firstRow = data[0];
  const colNames = ["validity", ...firstRow.map((d) => d.name)];

  const getCellStyle = (params: CellClassParams<any, any>) => {
    const colId = params.column.getColId();
    if (colId === "validity") {
      const hasError = params.data?.some((item: ValidationObj) => !item.valid);
      const hasWarning =
        !hasError && params.data?.some((item: ValidationObj) => !!item.warning);
      if (hasError) return { color: "red", backgroundColor: "unset" };
      if (hasWarning) return { color: "#92400e", backgroundColor: "#fffbeb" };
      return { color: "black", backgroundColor: "unset" };
    }
    if (!params?.value?.valid)
      return { color: "red", backgroundColor: "unset" };
    if (params?.value?.warning)
      return { color: "#92400e", backgroundColor: "#fffbeb" };
    return { color: "black", backgroundColor: "unset" };
  };

  const valueGetter = (params: ValueGetterParams<any, any>) => {
    const colId = params.column.getColId();
    if (colId === "validity") {
      const valid = isRowValid(params.data);
      const hasWarning =
        valid && params.data?.some((item: ValidationObj) => !!item.warning);
      return {
        name: "validity",
        valid,
        value: valid ? (hasWarning ? "Warning" : "Valid") : "Invalid",
        error: valid ? null : "Validation Error",
        warning: hasWarning ? "Some fields have warnings" : undefined,
      };
    }
    const item = params.data?.find(
      (item: ValidationObj) => item.name === colId
    ) || {
      value: undefined,
      valid: true,
    };
    if (
      item.value &&
      typeof item.value === "string" &&
      /^\d{4}-\d{2}-\d{2}/.test(item.value)
    ) {
      const [year, month, day] = item.value
        .substring(0, 10)
        .split("-")
        .map(Number);
      return {
        ...item,
        value: new Date(year, month - 1, day).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      };
    }
    if (isReasonColumnName(colId) && item.value && typeof item.value === "string") {
      return {
        ...item,
        value: getReasonDisplayLabel(item.value),
      };
    }
    return item;
  };

  const valueSetter = (params: ValueSetterParams) => {
    if (params.newValue === undefined || params.newValue === null) return false;
    // agDateCellEditor returns a Date object; normalize to ISO string for consistent storage
    let incomingValue = params.newValue;
    if (incomingValue instanceof Date) {
      const y = incomingValue.getFullYear();
      const m = String(incomingValue.getMonth() + 1).padStart(2, "0");
      const d = String(incomingValue.getDate()).padStart(2, "0");
      incomingValue = `${y}-${m}-${d}`;
    }
    let valueChanged = false;
    (params.data as ValidationObj[]).forEach((item) => {
      if (item.name === params.colDef.field) {
        const currentDisplay = getEffectiveDisplayValue(item.value);
        if (currentDisplay !== String(incomingValue ?? "")) {
          item.value = incomingValue;
          item.valid = true;
          item.error = null;
          valueChanged = true;
        }
      }
    });
    if (valueChanged) {
      params.node?.setData(params.data);
      const rows: ValidationObj[][] = [];
      gridApi.current?.forEachNode((node: any) => rows.push(node.data));
      const hasErr = rows.some((row) =>
        row.filter((d) => d.name !== "validity").some((d) => !d.valid)
      );
      onErrorsChange(hasErr);
      gridApi.current?.refreshCells();
    }
    return valueChanged;
  };

  return colNames.map((name, index) => {
    const isDateCol = name !== "validity" && isDateColumnName(name);
    const isReasonCol = name !== "validity" && isReasonColumnName(name);
    return {
      headerName: name,
      field: name,
      checkboxSelection: index === 0,
      headerCheckboxSelection: index === 0,
      valueGetter,
      valueSetter,
      cellRenderer: name === "validity" ? ValidityCellRenderer : CellRenderer,
      cellRendererParams: ({ value }: { value: any }) => value,
      cellStyle: getCellStyle,
      cellEditorSelector: isDateCol
        ? () => ({
            component: "agDateCellEditor",
            params: { min: "1900-01-01", max: "2100-12-31" },
          })
        : undefined,
      valueFormatter: (params: any) => {
        if (name === "validity")
          return (
            params.value?.value ?? (params.value?.valid ? "Valid" : "Invalid")
          );
        if (isDateCol) {
          const val = params.value?.value;
          if (!val || typeof val !== "string") return "";
          // Convert display date "01 Jan 2024" → ISO "2024-01-01" for agDateCellEditor
          const monthMap: Record<string, number> = {
            Jan: 1,
            Feb: 2,
            Mar: 3,
            Apr: 4,
            May: 5,
            Jun: 6,
            Jul: 7,
            Aug: 8,
            Sep: 9,
            Oct: 10,
            Nov: 11,
            Dec: 12,
          };
          const parts = val.split(" ");
          if (parts.length === 3 && monthMap[parts[1]]) {
            const d = parts[0].padStart(2, "0");
            const m = String(monthMap[parts[1]]).padStart(2, "0");
            return `${parts[2]}-${m}-${d}`;
          }
          if (/^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
          return "";
        }
        // Handle reason columns - display the label instead of value
        if (isReasonCol) {
          const val = params.value?.value;
          if (!val || typeof val !== "string") return "";
          return getReasonDisplayLabel(val);
        }
        return params.value?.value != null ? String(params.value.value) : "";
      },
      cellEditorParams: { useFormatter: true },
      filterValueGetter: (params: any) => {
        if (name === "validity") {
          const valid = isRowValid(params.data ?? []);
          const hasWarning =
            valid &&
            (params.data ?? []).some((d: ValidationObj) => !!d.warning);
          return hasWarning ? "Warning" : valid ? "Valid" : "Invalid";
        }
        const rawValue = params.data?.find(
          (d: ValidationObj) => d.name === name
        )?.value;
        // For reason columns, return the display label for filtering
        if (isReasonCol && rawValue && typeof rawValue === "string") {
          return getReasonDisplayLabel(rawValue);
        }
        return getEffectiveDisplayValue(rawValue);
      },
      headerClass: "text-sm font-normal capitalize",
      pinned: name === "validity" ? "left" : undefined,
      minWidth: 200,
      flex: 1,
      resizable: true,
      filter: true,
      autoHeight: true,
      editable: name !== "validity",
      menuTabs: ["filterMenuTab"],
    };
  });
}

const BulkInitiateResignation: React.FC = () => {
  const navigate = useNavigate();
  const { companyData } = useCompanyStore();

  const [file, setFile] = useState<File>();
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [resignationHasErrors, setResignationHasErrors] = useState(false);
  const [exerciseHasErrors, setExerciseHasErrors] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [canSelectedBeResubmitted, setCanSelectedBeResubmitted] =
    useState(false);
  const [needsRevalidation, setNeedsRevalidation] = useState(false);
  const [selectedResignationCount, setSelectedResignationCount] = useState(0);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const resignationGridApi = useRef<GridApi | any>(null);
  const exerciseGridApi = useRef<GridApi | any>(null);

  const { mutate: uploadResignation, isLoading: isUploading } =
    useUploadBulkResignationImport();

  const isPreviewMode = importResult !== null && !importResult.hasErrors;

  const handleDownloadTemplate = () => {
    getBulkResignationTemplate().then((uploadTemplate) => {
      const template = uploadTemplate.data.uploadTemplate;
      convertBase64ToBlob(
        template,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ).then((blobObject) => {
        downloadBlobObject(
          blobObject,
          `${companyData?.name} Bulk Resignation Template.xlsx`
        );
      });
    });
  };

  const handleUpload = async (fileToUpload: File) => {
    const base64Raw = (await convertToBase64(fileToUpload)) as string;
    const base64Content = base64Raw.includes(",")
      ? base64Raw.split(",")[1]
      : base64Raw;

    uploadResignation(
      { file: base64Content },
      {
        onSuccess: (data) => {
          setImportResult(data);
          setNeedsRevalidation(false);
          setSelectedResignationCount(0);
          if (!data.hasErrors) {
            setResignationHasErrors(false);
            setExerciseHasErrors(false);
          } else {
            const resErr = (data.resignationValidationData ?? []).some((row) =>
              row.filter((d) => d.name !== "validity").some((d) => !d.valid)
            );
            const exErr = data.exerciseValidationData.some((row) =>
              row.filter((d) => d.name !== "validity").some((d) => !d.valid)
            );
            setResignationHasErrors(resErr);
            setExerciseHasErrors(exErr);
          }
          if (data.message) {
            toast(data.message, {
              type: data.hasErrors ? "warning" : "success",
            });
          }
        },
        onError: (error: any) => {
          const errorData = error?.response?.data?.data;
          if (errorData?.resignationValidationData) {
            setImportResult(errorData);
          } else {
            toast(
              error?.response?.data?.message ||
                "Error processing resignation file",
              { type: "error" }
            );
          }
        },
      }
    );
  };

  const handleFileDrop = (dropped: File) => {
    setFile(dropped);
    setImportResult(null);
    setResignationHasErrors(false);
    setExerciseHasErrors(false);
    handleUpload(dropped);
  };

  const handleConfirm = () => {
    if (!importResult) return;

    const resignationRows: ValidationObj[][] =
      resignationGridApi.current?.getSelectedRows() ?? [];
    if (resignationRows.length === 0) {
      toast("Please select at least one employee to confirm resignation", {
        type: "warning",
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmProceed = () => {
    setShowConfirmDialog(false);
    if (!importResult) return;

    const resignationRows: ValidationObj[][] =
      resignationGridApi.current?.getSelectedRows() ?? [];
    const hrIdFieldName = resignationRows[0]?.find((d) =>
      d.name.toLowerCase().includes("hr")
    )?.name;
    const selectedHrIds = hrIdFieldName
      ? new Set(
          resignationRows
            .map(
              (row: ValidationObj[]) =>
                row.find((d) => d.name === hrIdFieldName)?.value
            )
            .filter(Boolean)
        )
      : null;

    const allExerciseRows: ValidationObj[][] = [];
    exerciseGridApi.current?.forEachNode((node: any) =>
      allExerciseRows.push(node.data)
    );
    const exerciseRows = allExerciseRows;

    setOperationInProgress(true);

    uploadResignation(
      {
        resignationValidatedData: resignationRows,
        ...(exerciseRows.length > 0
          ? { exerciseValidatedData: exerciseRows }
          : {}),
        confirm: true,
      },
      {
        onSuccess: (data) => {
          setOperationInProgress(false);
          toast(data.message || "Resignations initiated successfully", {
            type: "success",
          });
          navigate("/options/allEmployees");
        },
        onError: (error: any) => {
          setOperationInProgress(false);
          if (error?.response?.status === 400) {
            const msg =
              error?.response?.data?.errorMessage ||
              "Session expired. Please re-upload your file.";
            setFile(undefined);
            setImportResult(null);
            toast(msg, { type: "error" });
          } else {
            toast("Failed to initiate resignations. Please try again.", {
              type: "error",
            });
          }
        },
      }
    );
  };

  const handleResubmit = (all = true) => {
    if (!importResult) return;

    let resignationRows: ValidationObj[][] = [];
    if (all) {
      resignationGridApi.current?.forEachNode((node: any) =>
        resignationRows.push(node.data)
      );
    } else {
      resignationRows = resignationGridApi.current?.getSelectedRows() ?? [];
      if (resignationRows.length === 0) {
        toast("No rows selected", { type: "error", autoClose: 2000 });
        return;
      }
    }

    setOperationInProgress(true);

    const exerciseRows: ValidationObj[][] = [];
    exerciseGridApi.current?.forEachNode((node: any) =>
      exerciseRows.push(node.data)
    );

    uploadResignation(
      {
        resignationValidatedData: resignationRows,
        ...(exerciseRows.length > 0
          ? { exerciseValidatedData: exerciseRows }
          : {}),
      },
      {
        onSuccess: (data) => {
          setOperationInProgress(false);
          setImportResult(data);
          setSelectedResignationCount(0);
          setCanSelectedBeResubmitted(false);
          if (!data.hasErrors) {
            setResignationHasErrors(false);
            setExerciseHasErrors(false);
            setNeedsRevalidation(false);
          } else {
            const resErr = (data.resignationValidationData ?? []).some((row) =>
              row.filter((d) => d.name !== "validity").some((d) => !d.valid)
            );
            const exErr = data.exerciseValidationData.some((row) =>
              row.filter((d) => d.name !== "validity").some((d) => !d.valid)
            );
            setResignationHasErrors(resErr);
            setExerciseHasErrors(exErr);
          }
        },
        onError: (error: any) => {
          setOperationInProgress(false);
          toast(
            error?.response?.data?.message ||
              "Validation failed. Please try again.",
            { type: "error" }
          );
        },
      }
    );
  };

  const resignationColDefs = useMemo(
    () =>
      buildColumns(
        importResult?.resignationValidationData ?? [],
        resignationGridApi,
        setResignationHasErrors
      ),
    [importResult?.resignationValidationData]
  );

  const exerciseColDefs = useMemo(
    () =>
      buildColumns(
        importResult?.exerciseValidationData ?? [],
        exerciseGridApi,
        setExerciseHasErrors
      ),
    [importResult?.exerciseValidationData]
  );

  const hasAnyErrors =
    resignationHasErrors || exerciseHasErrors || needsRevalidation;

  const lwdWarnCount = (importResult?.resignationValidationData ?? []).filter(
    (row) =>
      row.some((cell) => cell.name === "Last Working Day" && !!cell.warning)
  ).length;

  // Upload screen
  if (!importResult) {
    return (
      <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Bulk Initiate Resignation
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Upload an Excel file to bulk initiate resignations.
            </p>
          </div>
          <ButtonPrimary
            className="flex items-center gap-2"
            onClick={handleDownloadTemplate}
          >
            <Icon icon="mdi:download-outline" height={18} />
            Download Template
          </ButtonPrimary>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="w-full max-w-2xl space-y-8">
            <div className="text-center space-y-2">
              <h3 className="text-xl font-medium text-gray-900">Upload File</h3>
              <p className="text-gray-500">
                Prepare your resignation data in the expected template and
                upload it here to preview the changes.
              </p>
            </div>

            <FileDropzone
              onFileSelect={handleFileDrop}
              selectedFile={file}
              onClear={() => setFile(undefined)}
            />

            <Instructions
              data={[
                "Use the provided template to ensure correct format",
                "Date fields should be in YYYY-MM-DD format",
                "Include employee identifier and resignation date for each row",
                "Do not include sensitive personal data in uploads",
                "Preview to verify rows before finalising the action",
                "This action only initiates the resignation process. To complete eligible resignations, navigate to the Stakeholders page and use the Bulk Actions option.",
              ]}
            />

            {isUploading && (
              <div className="flex flex-col items-center gap-3 py-4">
                <CircularProgress size={36} />
                <p className="text-sm text-gray-500">Validating file...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Preview / validation screen (mirrors GrantsImport layout)
  return (
    <div className="w-full bg-white border-2 rounded-md px-4">
      <HStack className="py-4 text-left bg-white items-start">
        <div className="flex-1">
          <h6 className="text-lg font-medium text-gray-800">
            Bulk Initiate Resignation
          </h6>
          <p className="text-sm text-gray-500 mt-1">
            Select employees to initiate their resignation process. Remember:
            you must complete resignations on the Stakeholder page.
          </p>
        </div>
        <ButtonPrimary onClick={handleDownloadTemplate}>
          Download Template
        </ButtonPrimary>
      </HStack>

      <div className="w-full flex flex-col gap-3 px-2 py-3 bg-white">
        {/* Resignation table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <HStack className="items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-base font-medium text-gray-800">
                  Resignations
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Review and select resignations to initiate
              </p>
            </div>
          </HStack>
          <ReversionWarningPanel
            warnings={importResult.reversionWarnings ?? []}
          />
          {lwdWarnCount > 0 && (
            <div className="mx-3 mb-3 mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
              <span className="mt-0.5 shrink-0 text-amber-500">⚠</span>
              <span className="text-sm text-amber-800">
                <span className="font-medium">
                  {lwdWarnCount} row{lwdWarnCount > 1 ? "s are" : " is"} missing
                  Last Working Day
                </span>
                , which is required by your company settings. You can still
                initiate, but those resignations cannot be completed until Last
                Working Day is provided.
              </span>
            </div>
          )}
          <div
            className="ag-theme-material"
            style={{
              height: `${
                (Math.min(importResult.resignationValidationData.length, 10) +
                  2) *
                60
              }px`,
            }}
          >
            <AgGridReact
              key={`RESIGNATION_GRID_${isPreviewMode ? "preview" : "edit"}`}
              columnDefs={resignationColDefs}
              rowData={importResult.resignationValidationData}
              stopEditingWhenCellsLoseFocus
              onGridReady={(params) => {
                resignationGridApi.current = params.api;
                params.api.sizeColumnsToFit();
              }}
              onCellValueChanged={() => setNeedsRevalidation(true)}
              onSelectionChanged={() => {
                const selected =
                  resignationGridApi.current?.getSelectedRows() ?? [];
                setSelectedResignationCount(selected.length);
                if (selected.length === 0) {
                  setCanSelectedBeResubmitted(false);
                  return;
                }
                const hasErr = (selected as ValidationObj[][]).some((row) =>
                  row.filter((d) => d.name !== "validity").some((d) => !d.valid)
                );
                setCanSelectedBeResubmitted(!hasErr);
              }}
              rowSelection="multiple"
              suppressRowClickSelection
              rowClass="border-t border-dashed"
              defaultColDef={{ flex: 1, minWidth: 150, resizable: true }}
            />
          </div>
          {selectedResignationCount > 0 && (
            <div className="px-3 py-2 bg-blue-50 border-t border-blue-100">
              <span className="text-sm text-blue-800">
                <span className="font-semibold">
                  {selectedResignationCount} resignation(s) selected
                </span>{" "}
                for initiation
              </span>
            </div>
          )}
        </div>

        {/* Exercise table (optional) */}
        {importResult.exerciseValidationData.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm mt-8">
            <HStack className="items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-base font-medium text-gray-800">
                Exercise Requests
              </p>
            </HStack>
            <div
              className="ag-theme-material"
              style={{
                height: `${
                  (Math.min(importResult.exerciseValidationData.length, 10) +
                    2) *
                  60
                }px`,
              }}
            >
              <AgGridReact
                key={`EXERCISE_GRID_${isPreviewMode ? "preview" : "edit"}`}
                columnDefs={exerciseColDefs}
                rowData={importResult.exerciseValidationData}
                stopEditingWhenCellsLoseFocus
                onGridReady={(params) => {
                  exerciseGridApi.current = params.api;
                  params.api.sizeColumnsToFit();
                }}
                onCellValueChanged={() => setNeedsRevalidation(true)}
                rowSelection="multiple"
                suppressRowClickSelection
                rowClass="border-t border-dashed"
                defaultColDef={{ flex: 1, minWidth: 150, resizable: true }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <HStack className="justify-between gap-4 py-2 px-1">
          <ButtonPrimary1
            type="reset"
            className="text-red-500"
            onClick={() => setShowDiscardDialog(true)}
          >
            Discard
          </ButtonPrimary1>

          <HStack className="justify-end gap-4">
            {hasAnyErrors && !operationInProgress && (
              <ButtonPrimary onClick={() => handleResubmit(true)}>
                Validate All
              </ButtonPrimary>
            )}
            {hasAnyErrors && !operationInProgress && (
              <ButtonPrimary
                disabled={!canSelectedBeResubmitted}
                onClick={() => handleResubmit(false)}
              >
                Validate Selected
              </ButtonPrimary>
            )}
            {hasAnyErrors && operationInProgress && (
              <Icon
                className="animate-spin text-orange-501"
                icon="lucide:loader-2"
                width={36}
              />
            )}

            {!hasAnyErrors && (
              <ButtonPrimary
                onClick={handleConfirm}
                disabled={operationInProgress || selectedResignationCount === 0}
              >
                {operationInProgress ? (
                  <div className="flex items-center gap-2">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Confirming...</span>
                  </div>
                ) : (
                  `Confirm Resignations${
                    selectedResignationCount > 0
                      ? ` (${selectedResignationCount})`
                      : ""
                  }`
                )}
              </ButtonPrimary>
            )}
          </HStack>
        </HStack>

        <AlertDialog
          open={showDiscardDialog}
          message="Are you sure you want to discard? The uploaded data will be lost and no resignations will be initiated."
          primaryActionText="Discard"
          secondaryActionText="Cancel"
          onPrimaryAction={() => {
            setShowDiscardDialog(false);
            setImportResult(null);
            setFile(undefined);
            setResignationHasErrors(false);
            setExerciseHasErrors(false);
            setNeedsRevalidation(false);
            setSelectedResignationCount(0);
            setCanSelectedBeResubmitted(false);
          }}
          onSecondaryAction={() => setShowDiscardDialog(false)}
          onClose={() => setShowDiscardDialog(false)}
        />

        <AlertDialog
          open={showConfirmDialog}
          message={`You are about to initiate the resignation process for ${selectedResignationCount} employee${
            selectedResignationCount > 1 ? "s" : ""
          }. Once confirmed, the resignation${
            selectedResignationCount > 1 ? "s" : ""
          } will be recorded as initiated. Please navigate to the Stakeholders page and use the Bulk Actions option to complete each resignation.`}
          primaryActionText="Confirm"
          secondaryActionText="Cancel"
          onPrimaryAction={handleConfirmProceed}
          onSecondaryAction={() => setShowConfirmDialog(false)}
          onClose={() => setShowConfirmDialog(false)}
        />

        {/* Two-step process info */}
        <div className="border border-blue-100 bg-blue-50 rounded-lg p-4 mt-2">
          <div className="flex items-center gap-2 mb-3">
            <Icon
              icon="mdi:information-outline"
              className="text-blue-500"
              height={18}
            />
            <span className="text-sm font-semibold text-blue-800">
              How the Two-Step Process Works
            </span>
          </div>
          <div className="flex flex-col">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold w-5 h-5 shrink-0 mt-0.5">
                1
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Initiate Here
                </p>
                <p className="text-xs text-gray-500">
                  Select and confirm resignations on this page
                </p>
              </div>
            </div>
            <div className="flex justify-start ml-0.5 my-1">
              <Icon
                icon="mdi:arrow-down"
                className="text-blue-400"
                height={16}
              />
            </div>
            <div className="flex items-start gap-3 mt-2">
              <span className="inline-flex items-center justify-center rounded-full bg-green-600 text-white text-xs font-bold w-5 h-5 shrink-0 mt-0.5">
                2
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Complete on Stakeholder Page
                </p>
                <p className="text-xs text-gray-500">
                  Navigate to Stakeholder page and complete resignations for
                  each selected employee
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInitiateResignation;
